import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle, Gift } from 'lucide-react';
import Toast from '../components/Toast';
import NotFound from './NotFound';
import { parseLinks } from '../utils/parseLinks';
import { ICONS } from '../utils/icons';
import { buildReceiptMetadata } from '../utils/receipt';

const FeatureIcon: React.FC<{ name?: string; className?: string }> = ({ name, className }) => {
  if (!name) return <CheckCircle className={className} />;
  const Icon = ICONS[name];
  if (Icon) return <Icon className={className} />;
  return <span className={className}>{name}</span>;
};

interface LandingFeature {
  icon?: string;
  text: string;
}

interface LandingConfig {
  id: number;
  slug: string;
  image_url: string | null;
  title_top: string | null;
  title_main: string | null;
  price_highlight: string | null;
  price_old: string | null;
  button_text: string;
  plan_name: string;
  price: number;
  checkbox1_text: string | null;
  checkbox2_text: string | null;
  features_title: string | null;
  features_json: string | null;
  bonus_title: string | null;
  bonus_subtitle: string | null;
  show_promo: boolean;
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const DynamicLanding: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [config, setConfig] = useState<LandingConfig | null>(null);
  const [status, setStatus] = useState<'loading' | 'ok' | 'not-found'>('loading');

  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [agree1, setAgree1] = useState(false);
  const [agree2, setAgree2] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info'; isVisible: boolean }>({
    message: '',
    type: 'info',
    isVisible: false,
  });

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    fetch(`${API_URL}/api/landings/by-slug/${slug}`)
      .then(async (r) => {
        if (cancelled) return;
        if (r.status === 404) {
          setStatus('not-found');
          return;
        }
        if (!r.ok) {
          setStatus('not-found');
          return;
        }
        const data = (await r.json()) as LandingConfig;
        setConfig(data);
        setStatus('ok');
        // Прячем slug из адресной строки — остаётся просто gekata.net/.
        // React Router не знает о replaceState, поэтому продолжает рендерить лендинг.
        window.history.replaceState(null, '', '/');
      })
      .catch(() => {
        if (!cancelled) setStatus('not-found');
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const showToast = (message: string, type: 'success' | 'error' | 'info') =>
    setToast({ message, type, isVisible: true });
  const hideToast = () => setToast((p) => ({ ...p, isVisible: false }));

  const validateEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setEmail(v);
    setEmailError(v && !validateEmail(v) ? 'Введите корректный email адрес' : '');
  };

  const handlePay = async () => {
    if (!config) return;
    if (!email) {
      setEmailError('Email обязателен для заполнения');
      return;
    }
    if (!validateEmail(email)) {
      setEmailError('Введите корректный email адрес');
      return;
    }
    if (!agree1 || !agree2) return;

    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/payment/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, plan: config.plan_name }),
      });
      const data = await response.json();

      if (data.status === 'success') {
        const widget = new (window as any).cp.CloudPayments();
        const public_id = process.env.REACT_APP_CLOUDPAYMENTS_PUBLIC_ID || 'pk_test';
        const description = `Тариф "${config.plan_name}"`;
        widget
          .start({
            publicTerminalId: public_id,
            description,
            amount: config.price,
            currency: 'RUB',
            userInfo: { accountId: email },
            accountId: email,
            skin: 'modern',
            tokenize: true,
            externalId: data.payment_id,
            paymentSchema: 'Single',
            receiptEmail: email,
            // Чек по 54-ФЗ — касса пробьёт его и отправит на email покупателя
            metadata: buildReceiptMetadata({ label: description, amount: config.price, email }),
          })
          .then((widgetResult: any) => {
            if (widgetResult.success) {
              showToast(
                'Оплата прошла успешно!\n\nПроверьте свою почту — мы отправили данные для входа.',
                'success'
              );
            }
          })
          .catch(() => showToast('Произошла ошибка при оплате. Попробуйте позже.', 'error'));
      } else {
        showToast('Произошла ошибка при создании платежа.', 'error');
      }
    } catch {
      showToast('Произошла ошибка при создании платежа.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  if (status === 'loading') {
    return (
      <section className="pt-24 min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-mystic-400 border-t-transparent rounded-full animate-spin" />
      </section>
    );
  }
  if (status === 'not-found' || !config) {
    return <NotFound />;
  }

  const features: LandingFeature[] = (() => {
    if (!config.features_json) return [];
    try {
      const parsed = JSON.parse(config.features_json);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  })();

  const canPay = email && agree1 && agree2 && !emailError && !isLoading;
  const imgSrc = config.image_url
    ? config.image_url.startsWith('http')
      ? config.image_url
      : `${API_URL}${config.image_url}`
    : null;

  return (
    <section className="px-4 pt-20 pb-8 min-h-screen flex items-center">
      <div className="max-w-xl mx-auto w-full">
        {/* Заголовки над картинкой, на тёмном фоне страницы */}
        {(config.title_top || config.title_main) && (
          <div className="text-center mb-3 px-2">
            {config.title_top && (
              <div className="font-display tracking-wide text-white text-base sm:text-lg uppercase font-semibold mb-1">
                {config.title_top}
              </div>
            )}
            {config.title_main && (
              <div className="font-display tracking-wide text-white text-xl sm:text-2xl uppercase font-bold">
                {config.title_main}
              </div>
            )}
          </div>
        )}

        {/* Картинка-баннер: чистая, без оверлеев */}
        {imgSrc && (
          <div className="relative rounded-2xl overflow-hidden mb-2 border border-[var(--primary-color-800-30)] h-40 sm:h-48 md:h-56 bg-lunar-900">
            <img src={imgSrc} alt="" className="absolute inset-0 w-full h-full object-cover" />
          </div>
        )}

        {/* Карточка с email + кнопка + чекбоксы */}
        <div className="bg-lunar-800/50 backdrop-blur-sm rounded-2xl p-4 border border-[var(--primary-color-800-30)] mb-2">
          <label className="block text-lunar-300 text-xs mb-1">Ваш Email для регистрации</label>
          <input
            type="email"
            value={email}
            onChange={handleEmailChange}
            placeholder="Введите электронную почту"
            className={`w-full px-3 py-2 bg-lunar-900 border rounded-lg text-white placeholder-lunar-400 focus:outline-none transition-colors text-sm ${
              emailError
                ? 'border-red-500 focus:border-red-400'
                : 'border-[var(--primary-color-800-30)] focus:border-[var(--primary-color-500)]'
            }`}
          />
          {emailError && <p className="text-red-400 text-xs mt-1">{emailError}</p>}

          <button
            onClick={handlePay}
            disabled={!canPay}
            className={`w-full py-2.5 mt-2 rounded-lg font-semibold text-white transition-all duration-300 ${
              canPay
                ? 'bg-green-600 hover:bg-green-700'
                : 'bg-lunar-700 text-lunar-400 cursor-not-allowed'
            }`}
          >
            {isLoading ? 'Создание платежа...' : `${config.button_text} ${config.price}₽`}
          </button>

          <div className="space-y-1.5 mt-2.5">
            {config.checkbox1_text && (
              <label className="flex items-start space-x-2 cursor-pointer">
                <CheckboxBox checked={agree1} onChange={setAgree1} />
                <span className="text-[11px] text-lunar-300 leading-snug">
                  {parseLinks(config.checkbox1_text)}
                </span>
              </label>
            )}
            {config.checkbox2_text && (
              <label className="flex items-start space-x-2 cursor-pointer">
                <CheckboxBox checked={agree2} onChange={setAgree2} />
                <span className="text-[11px] text-lunar-300 leading-snug">
                  {parseLinks(config.checkbox2_text)}
                </span>
              </label>
            )}
          </div>
        </div>

        {/* Блок "Акция!" — показываем только если включён в админке */}
        {config.show_promo && (config.features_title || features.length > 0 || config.bonus_title) && (
          <div className="bg-lunar-800/50 backdrop-blur-sm rounded-2xl p-4 border border-[var(--primary-color-800-30)] relative">
            <span className="absolute top-2.5 right-2.5 text-[10px] bg-mystic-gradient text-white px-2 py-0.5 rounded-full">
              Акция!
            </span>
            {config.features_title && (
              <h3 className="text-center text-white text-sm font-semibold mb-2">{config.features_title}</h3>
            )}
            {features.length > 0 && (
              <div className="grid sm:grid-cols-2 gap-1.5 mb-2.5">
                {features.map((f, i) => (
                  <div key={i} className="flex items-start text-lunar-300 text-xs">
                    <FeatureIcon name={f.icon} className="w-4 h-4 text-mystic-400 mr-1.5 mt-0.5 flex-shrink-0" />
                    <span>{f.text}</span>
                  </div>
                ))}
              </div>
            )}
            {config.bonus_title && (
              <div className="bg-lunar-900/80 border border-[var(--primary-color-800-30)] rounded-lg p-2.5 flex items-center">
                <div className="w-9 h-9 bg-mystic-gradient rounded-full mr-2.5 flex-shrink-0 flex items-center justify-center">
                  <Gift className="w-4 h-4 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-white font-semibold text-xs">{config.bonus_title}</div>
                  {config.bonus_subtitle && (
                    <div className="text-lunar-400 text-[11px]">{config.bonus_subtitle}</div>
                  )}
                </div>
                <div className="text-green-400 font-semibold text-xs ml-2">БЕСПЛАТНО ✓</div>
              </div>
            )}
          </div>
        )}
      </div>

      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={hideToast}
      />
    </section>
  );
};

const CheckboxBox: React.FC<{ checked: boolean; onChange: (v: boolean) => void }> = ({
  checked,
  onChange,
}) => (
  <div className="relative flex-shrink-0 mt-0.5">
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      className="sr-only"
    />
    <div
      className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
        checked
          ? 'bg-mystic-600 border-mystic-600'
          : 'border-[var(--primary-color-800-50)] bg-lunar-900'
      }`}
    >
      {checked && (
        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      )}
    </div>
  </div>
);

export default DynamicLanding;
